// "Print QR by Box ID", launched from the Inventory page.
//
// The warehouse types a box barcode (RB-XXXXXXXX, off the box sticker), we
// resolve it to the box and the items in it, and print each tracked item's QR
// sheet on the drone-sticker roll stock (printBoxInstanceQRLabels), one sheet per
// item so the START/END tiles name the item they bracket.
//
// A box holding exactly one tracked item prints straight away and clears the
// field, so a run of single-item boxes is still type, Enter, next.

import React, { useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Panel } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import ItemName, { itemLabel } from '../components/ItemName';
import { modalLabel } from '../components/review/fieldStyles';
import DrawerShell from './DrawerShell';
import Reveal from '../components/ui/Reveal';
import { printBoxInstanceQRLabels } from '../utils/printing-utils';
import type { BoxByBarcode } from './types';

type BoxItem = BoxByBarcode['items'][number];

interface Props {
  onClose: () => void;
}


const unassembled = (item: BoxItem) => item.instances.filter((i) => i.status === 'in_stock').length;

const PrintBoxQrModal: React.FC<Props> = ({ onClose }) => {
  const api = useApi();
  const toast = useToast();
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [box, setBox] = useState<BoxByBarcode | null>(null);
  const [printingId, setPrintingId] = useState<string | null>(null);

  const printItem = async (resolved: BoxByBarcode, item: BoxItem, onlyUnassembled = false): Promise<boolean> => {
    const instances = onlyUnassembled ? item.instances.filter((i) => i.status === 'in_stock') : item.instances;
    if (instances.length === 0) {
      toast.warning(`${itemLabel(item)} in ${resolved.barcode} has no item records to print.`);
      return false;
    }
    await printBoxInstanceQRLabels(instances, {
      sku: itemLabel(item),
      barcode: resolved.barcode,
    });
    toast.success(
      `Printing ${instances.length} QR label${instances.length === 1 ? '' : 's'} for ${itemLabel(item)}`
    );
    return true;
  };

  const handleLookup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const value = barcode.trim();
    if (!value || loading) return;

    setLoading(true);
    setBox(null);
    try {
      const resolved = (await api.get(
        `/receipt-boxes/by-barcode/${encodeURIComponent(value)}`
      )) as BoxByBarcode;
      const tracked = resolved.items.filter((i) => i.tracked_per_instance);

      // Valid boxes with nothing to print are a normal outcome, not an error.
      if (tracked.length === 0) {
        toast.warning(`${resolved.barcode}: nothing in this box is tracked per unit, so there are no item QRs to print.`);
        setBox(resolved);
        return;
      }
      // A part-used item needs a choice: all QRs or only the unassembled ones.
      const partUsed = tracked[0].instances.some((i) => i.status !== 'in_stock');
      if (resolved.items.length === 1 && !partUsed) {
        if (await printItem(resolved, tracked[0])) setBarcode('');
        return;
      }
      setBox(resolved);
    } catch (err: any) {
      if (err?.status === 404) {
        toast.error(`No box found for "${value}". Check the sticker and type it again.`);
      } else {
        toast.error(err?.message || 'The lookup failed. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrintItem = async (item: BoxItem, onlyUnassembled = false) => {
    if (!box) return;
    setPrintingId(item.id);
    try {
      await printItem(box, item, onlyUnassembled);
    } catch (err: any) {
      toast.error(err?.message || 'The QR sheet did not print. Try again.');
    } finally {
      setPrintingId(null);
    }
  };

  const startOver = () => {
    setBox(null);
    setBarcode('');
  };

  return (
    <DrawerShell title="Print QR by Box ID" onClose={onClose}>
      <div className="p-6 space-y-4">
        <form onSubmit={handleLookup} className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Type the barcode from the box sticker. Each tracked item in the box prints its own
            QR sheet on the roll printer, bookended with <span className="font-mono">START</span>{' '}
            and <span className="font-mono">END</span> tiles that name the item.
          </p>

          <div>
            <label htmlFor="print-box-barcode" className={modalLabel}>
              Box barcode
            </label>
            <Field
              id="print-box-barcode"
              type="text"
              thumb
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="RB-XXXXXXXX"
              data-drawer-autofocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={loading}
              className="font-mono uppercase"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={buttonClass('quiet', 'md')}
            >
              Close
            </button>
            <button
              type="submit"
              disabled={!barcode.trim() || loading}
              className={buttonClass('filled', 'md')}
            >
              {loading ? 'Looking up…' : 'Find and print'}
            </button>
          </div>
        </form>

        <Reveal show={!!box}>
        {box && (
          <Panel
            level={3}
            padding="sm"
            title={<>In <span className="font-mono">{box.barcode}</span></>}
            actions={(
              <button
                type="button"
                onClick={startOver}
                className={buttonClass('quiet', 'sm')}
              >
                Another box
              </button>
            )}
          >
            <ListStack
              items={box.items}
              getKey={(item) => item.id}
              on="panel"
              spacing="loose"
              renderRow={(item) => (
                <ListRow
                  actions={item.tracked_per_instance ? (
                    <div className="flex shrink-0 items-center gap-2">
                      {unassembled(item) > 0 && unassembled(item) < item.instances.length && (
                        <button
                          type="button"
                          onClick={() => handlePrintItem(item, true)}
                          disabled={printingId !== null}
                          aria-label={`Print unassembled QRs for ${itemLabel(item)}`}
                          className={`${buttonClass('quiet', 'sm')} shrink-0`}
                        >
                          Print unassembled ({unassembled(item)})
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handlePrintItem(item)}
                        disabled={printingId !== null}
                        aria-label={`Print item QRs for ${itemLabel(item)}`}
                        className={`${buttonClass('quiet', 'sm')} shrink-0`}
                      >
                        {printingId === item.id ? 'Printing…' : `Print item QRs (${item.instances.length})`}
                      </button>
                    </div>
                  ) : (
                    <span className="shrink-0 whitespace-nowrap text-xs text-on-surface-variant">
                      No item QRs
                    </span>
                  )}
                >
                  <div className="min-w-0">
                    <ItemName size="sm" item={item} />
                    <span className="text-xs text-on-surface-variant tabular-nums">
                      {item.quantity_remaining} of {item.quantity_received} left
                    </span>
                  </div>
                </ListRow>
              )}
            />
          </Panel>
        )}
        </Reveal>
      </div>
    </DrawerShell>
  );
};

export default PrintBoxQrModal;
